import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useState } from "react";
import { Button, Loading, Panel, Typography } from "@/components/atoms";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useLazyGetPaymentMethodQuery } from "@/redux/apis/paymentMethod/paymentMethodApi";
import { useCountryConfig } from "@/hooks";
import { addToast } from "@/utils";

export const PaymentMethodSetupPanel = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [getPaymentMethod, { isFetching }] = useLazyGetPaymentMethodQuery();

  const url = `${getPhpHostUrl()}/passenger/booking`;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isStripeLoading = !stripe || !elements || isFetching;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isStripeLoading) return;

    setIsSubmitting(true);
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: url },
      redirect: "if_required",
    });

    if (result.error) {
      addToast("danger", result.error.message || "Something went wrong. Please contact your administrator.");
      setIsSubmitting(false);
    } else {
      setTimeout(() => {
        getPaymentMethod(result.setupIntent.id)
          .unwrap()
          .then(() => {
            addToast("success", "Successfully added new payment method");
            window.location.href = url;
          })
          .catch((e) => {
            getErrorMessages(e).forEach((m) => addToast("danger", m));
            setIsSubmitting(false);
          });
      }, 8000);
    }
  };

  /** Follows the operator's country rather than assuming Australian BECS. */
  const { config } = useCountryConfig();
  const paymentMethodOrder = ["card", config.stripeDebitPaymentMethod].filter(Boolean) as string[];

  return (
    <Panel className="relative flex h-full w-[550px] flex-col">
      {(isSubmitting || isStripeLoading) && <Loading />}
      <form id="payment-method-form" onSubmit={handleSubmit}>
        <div className="flex flex-col">
          <Typography variant="h3">Add New Payment Method</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Fill up your card information
          </Typography>
        </div>
        <div className="mt-2 flex flex-col">
          <PaymentElement
            options={{
              paymentMethodOrder: paymentMethodOrder,
              layout: {
                type: "tabs",
                defaultCollapsed: false,
              },
            }}
          />
        </div>
        <Button variant="primary" size="lg" className="mt-8 w-full" type="submit" disabled={isStripeLoading}>
          Save Payment Method
        </Button>
      </form>
    </Panel>
  );
};
